import { Components, Theme, alpha } from '@mui/material/styles';
// Load DataGrid theme typing so the `MuiDataGrid` override key is recognised.
import type {} from '@mui/x-data-grid/themeAugmentation';
import { getCssVars } from './palette';

export const getComponents = (theme: Theme): Components<Theme> => {
  const { palette } = theme;
  const isLight = palette.mode === 'light';
  const cssVars = getCssVars(palette.mode);

  // Slightly stronger than divider — used for input/control outlines.
  const borderStrong = isLight ? '#DAD5CB' : '#3D3930';
  // Subtle tinted header surface (tables / data grid).
  const headerBg = isLight ? '#F4F2EE' : '#1C1B18';
  const focusRing = `0 0 0 3px ${alpha(palette.primary.main, isLight ? 0.25 : 0.35)}`;
  const tooltipBg = isLight ? '#34322D' : '#3A362F';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': cssVars,
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          scrollbarColor: `${isLight ? '#C9C3B8' : '#3D3930'} transparent`,
        },
        body: {
          backgroundColor: palette.background.default,
          color: palette.text.primary,
          transition: 'background-color 200ms ease, color 200ms ease',
        },
        '::selection': { backgroundColor: alpha(palette.primary.main, 0.25) },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: isLight ? '#D2CEC4' : '#3D3930',
          borderRadius: 8,
          border: `2px solid ${palette.background.default}`,
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: isLight ? '#B4AFA3' : '#4E4A42',
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' }, // kill MUI's dark-mode lightening overlay
        outlined: { borderColor: palette.divider },
        rounded: { borderRadius: 12 },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: `1px solid ${palette.divider}`,
          backgroundImage: 'none',
          boxShadow: theme.shadows[1],
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 20, '&:last-child': { paddingBottom: 20 } },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          paddingInline: 16,
          minHeight: 40,
          fontWeight: 700,
          transition:
            'background-color 160ms ease, box-shadow 160ms ease, border-color 160ms ease, color 160ms ease',
          '&.Mui-focusVisible': { boxShadow: focusRing },
        },
        sizeSmall: { minHeight: 34, paddingInline: 12, fontSize: '0.8125rem' },
        sizeLarge: { minHeight: 48, paddingInline: 24, fontSize: '0.9375rem' },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
        containedPrimary: { '&:hover': { backgroundColor: palette.primary.dark } },
        // Neutral / secondary buttons use color="inherit" — styled here so they
        // read as calm, while semantic colors (error/success/warning) keep their
        // own MUI tints untouched.
        outlinedInherit: {
          borderColor: borderStrong,
          color: palette.text.primary,
          '&:hover': {
            borderColor: palette.text.primary,
            backgroundColor: palette.action.hover,
          },
        },
        textInherit: {
          color: palette.text.primary,
          '&:hover': { backgroundColor: palette.action.hover },
        },
        outlinedPrimary: {
          borderColor: alpha(palette.primary.main, 0.5),
          '&:hover': {
            borderColor: palette.primary.main,
            backgroundColor: alpha(palette.primary.main, 0.06),
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'background-color 160ms ease, color 160ms ease',
          '&.Mui-focusVisible': { boxShadow: focusRing },
        },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 700,
          borderColor: borderStrong,
          color: palette.text.secondary,
          '&.Mui-selected': {
            backgroundColor: alpha(palette.primary.main, isLight ? 0.12 : 0.2),
            color: palette.text.primary,
            '&:hover': {
              backgroundColor: alpha(palette.primary.main, isLight ? 0.18 : 0.26),
            },
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 600 },
        filled: {
          backgroundColor: isLight ? '#EFEDE7' : '#2A2823',
          color: palette.text.primary,
        },
        outlined: { borderColor: borderStrong },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: tooltipBg,
          color: '#F4F1EA',
          fontSize: '0.75rem',
          fontWeight: 600,
          borderRadius: 8,
          padding: '6px 10px',
          boxShadow: theme.shadows[3],
        },
        arrow: { color: tooltipBg },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: palette.background.paper,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: borderStrong },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.text.secondary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.primary.main,
            borderWidth: 1.5,
          },
        },
        input: {
          '&::placeholder': { color: palette.text.disabled, opacity: 1 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { '&.Mui-focused': { color: palette.primary.main } },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${palette.divider}`,
          boxShadow: theme.shadows[6],
          marginTop: 4,
        },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.875rem',
          '&:hover': { backgroundColor: palette.action.hover },
          '&.Mui-selected': {
            backgroundColor: alpha(palette.primary.main, isLight ? 0.1 : 0.18),
            '&:hover': {
              backgroundColor: alpha(palette.primary.main, isLight ? 0.16 : 0.24),
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-selected': {
            backgroundColor: alpha(palette.primary.main, isLight ? 0.1 : 0.18),
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${palette.divider}`,
          boxShadow: theme.shadows[6],
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: `1px solid ${palette.divider}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontSize: '1.125rem', fontWeight: 800 } },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: { backgroundColor: alpha('#0E0D0B', isLight ? 0.4 : 0.6) },
        invisible: { backgroundColor: 'transparent' },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: palette.divider } },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: headerBg,
            color: palette.text.secondary,
            fontWeight: 700,
            fontSize: '0.8125rem',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: { root: { borderColor: palette.divider } },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: palette.action.hover } },
      },
    },

    MuiTabs: {
      styleOverrides: { indicator: { height: 3, borderRadius: 3 } },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.875rem',
          minHeight: 44,
          color: palette.text.secondary,
          '&.Mui-selected': { color: palette.primary.main },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        switchBase: { '&.Mui-checked': { color: palette.primary.main } },
        track: { borderRadius: 12 },
      },
    },
    MuiCheckbox: { styleOverrides: { root: { color: borderStrong } } },
    MuiRadio: { styleOverrides: { root: { color: borderStrong } } },

    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: { root: { color: palette.primary.main, fontWeight: 600 } },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12, border: '1px solid transparent' },
        standardSuccess: {
          backgroundColor: alpha(palette.success.main, isLight ? 0.1 : 0.16),
          borderColor: alpha(palette.success.main, 0.24),
          color: isLight ? palette.success.dark : palette.success.main,
        },
        standardError: {
          backgroundColor: alpha(palette.error.main, isLight ? 0.1 : 0.16),
          borderColor: alpha(palette.error.main, 0.24),
          color: isLight ? palette.error.dark : palette.error.main,
        },
        standardWarning: {
          backgroundColor: alpha(palette.warning.main, isLight ? 0.1 : 0.16),
          borderColor: alpha(palette.warning.main, 0.24),
          color: isLight ? palette.warning.dark : palette.warning.main,
        },
        standardInfo: {
          backgroundColor: alpha(palette.info.main, isLight ? 0.1 : 0.16),
          borderColor: alpha(palette.info.main, 0.24),
          color: isLight ? palette.info.dark : palette.info.main,
        },
      },
    },

    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          '&.Mui-selected': {
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            '&:hover': { backgroundColor: palette.primary.dark },
          },
        },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        // Flat by default — accordions in this app are nested list items inside
        // cards/builders, so a default border/radius would create a box-in-a-box.
        // The parent surface provides the border; the accordion blends in.
        root: {
          backgroundImage: 'none',
          backgroundColor: 'transparent',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: isLight ? '#EFEDE7' : '#2A2823' },
      },
    },

    // MUI X DataGrid — operator workhorse (Answers / Users / Guests tables)
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${palette.divider}`,
          borderRadius: 14,
          backgroundColor: palette.background.paper,
          '--DataGrid-rowBorderColor': palette.divider,
          '--DataGrid-containerBackground': headerBg,
        },
        columnHeaders: { borderBottom: `1px solid ${palette.divider}` },
        columnHeader: { '&:focus, &:focus-within': { outline: 'none' } },
        columnHeaderTitle: {
          fontWeight: 700,
          fontSize: '0.8125rem',
          color: palette.text.secondary,
        },
        cell: {
          borderColor: palette.divider,
          '&:focus, &:focus-within': { outline: 'none' },
        },
        row: {
          '&:hover': { backgroundColor: palette.action.hover },
          '&.Mui-selected': {
            backgroundColor: alpha(palette.primary.main, isLight ? 0.1 : 0.18),
            '&:hover': {
              backgroundColor: alpha(palette.primary.main, isLight ? 0.14 : 0.22),
            },
          },
        },
        footerContainer: { borderTop: `1px solid ${palette.divider}` },
      },
    },
  };
};
